import React from 'react';

import { Navigate } from 'react-router-dom';

import { useProtectedRouteLogic } from 'components/common/ProtectedRoute/useProtectedRouteLogic';

interface ProtectedRouteV2Props {
  element: React.ReactNode;
  requiredPermission: string;
}

/**
 * Protected route component that checks basic authorization.
 *
 * This component protects routes that require general authorization without
 * team-specific or resource-specific checks. It only validates that the user
 * has the required permission at the organization level.
 *
 * @param element - The React element to render if authorization passes
 * @param requiredPermission - The permission string required to access this route
 *
 * @example
 * ```tsx
 * <ProtectedRouteV2
 *   element={<AdminPanel />}
 *   requiredPermission="manage_teams"
 * />
 * ```
 */
const ProtectedRouteV2 = ({
  element,
  requiredPermission,
}: ProtectedRouteV2Props) => {
  const { isLoading, shouldRedirect } = useProtectedRouteLogic({
    requiredPermission,
  });

  if (isLoading) {
    return null;
  }

  if (shouldRedirect) {
    return (
      <Navigate
        replace
        to={{ pathname: `/unauthorized/${requiredPermission}` }}
      />
    );
  }

  return element;
};

export default ProtectedRouteV2;
